<template>
  <textarea
    ref="textarea"
    v-model="value"
    :rows="rows"
    :disabled="disabled"
    :placeholder="placeholder"
    :class="`
		  block
      outline-none border-0
		  text-sm bg-transparent text-dlubal-blue placeholder-gray-30
	    disabled:text-gray-60
      transition-colors duration-300 ease-in-out
      resize-none overflow-auto
    `"
    @change="handleChange"
    @input="handleInput"
  ></textarea>
</template>

<script setup lang="ts">
import { watch, nextTick, useTemplateRef } from 'vue'
import { useVModel } from '@vueuse/core'

export interface UTextareaProps {
  modelValue: string
  rows?: number
  maxRows?: number
  placeholder?: string
  disabled?: boolean
}

interface UTextareaEmits {
  (e: 'update:modelValue', modelValue: string): void
  (e: 'change', value: string): void
  (e: 'input', value: string): void
}

const props = withDefaults(defineProps<UTextareaProps>(), {
  modelValue: '',
  rows: 1,
  maxRows: 1,
  placeholder: '',
  disabled: false
})
const textareaRef = useTemplateRef<HTMLTextAreaElement>('textarea')

const value = useVModel(props, 'modelValue')
const emit = defineEmits<UTextareaEmits>()

const autoResize = () => {
  if (textareaRef.value === null) {
    return
  }

  textareaRef.value.rows = props.rows

  const styles = window.getComputedStyle(textareaRef.value)
  const paddingTop = parseInt(styles.paddingTop)
  const paddingBottom = parseInt(styles.paddingBottom)
  const padding = paddingTop + paddingBottom
  const lineHeight = parseInt(styles.lineHeight)
  const newRows = Math.ceil((textareaRef.value.scrollHeight - padding) / lineHeight)

  if (newRows > props.rows) {
    textareaRef.value.rows = Math.min(newRows, props.maxRows)
  }
}

const handleChange = (event: Event) => {
  emit('change', (event.target as HTMLInputElement).value)
}

const handleInput = (event: Event) => {
  emit('input', (event.target as HTMLInputElement).value)
  emit('update:modelValue', (event.target as HTMLInputElement).value)
  autoResize()
}

watch(value, () => {
  nextTick(autoResize)
})
</script>
